/**
 * CloseTab — Batch #14.325 : clôture guidée (fin de mois · fin d'exercice).
 * Sélecteur de période, liste de contrôle (étapes automatiques vérifiées dans les données + étapes à cocher), actions
 * (écriture d'amortissement, écriture de fermeture, dossier PDF / Drive), clôture (verrouillage) et réouverture motivée.
 */
import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, CalendarDays, FileDown, Lock, Unlock, UploadCloud } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Modal, Pill, downloadPdf, errMsg, inputCls, money, useGlBase } from "../../comptabilite/_shared";
import DepreciationDialog from "../assets/DepreciationDialog";
import CloseStep from "./CloseStep";

function fmtPeriod(kind, end) {
  if (kind === "month") { const [y, m] = end.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("fr-CA", { month: "long", year: "numeric" }); }
  return `Exercice se terminant le ${end}`;
}

export default function CloseTab({ scope, goTab, refresh }) {
  const base = useGlBase();
  const [periods, setPeriods] = useState(null);
  const [kind, setKind] = useState("month");
  const [end, setEnd] = useState("");
  const [cl, setCl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [depr, setDepr] = useState(false);
  const [reopen, setReopen] = useState(false);
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(false);

  const loadPeriods = useCallback(async () => {
    try {
      const r = await api.get(`${base}/close/periods`);
      setPeriods(r.data);
      setEnd((e) => e || r.data.months[0]?.period_end || "");
    } catch (e) { toast.error(errMsg(e, "Périodes indisponibles")); }
  }, [base]);
  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const load = useCallback(async () => {
    if (!end) return;
    setCl(null);
    try { const r = await api.get(`${base}/close/${kind}/${end}`); setCl(r.data); }
    catch (e) { toast.error(errMsg(e, "Liste de contrôle indisponible")); }
  }, [base, kind, end]);
  useEffect(() => { load(); }, [load]);
  const after = (data) => { setCl(data); loadPeriods(); refresh?.(); };

  const pickKind = (k) => { setKind(k); setEnd(k === "month" ? periods?.months[0]?.period_end : periods?.years[0]?.period_end); };
  const setStep = async (id, done, note) => {
    try { const r = await api.put(`${base}/close/${kind}/${end}/step/${id}`, { done, note }); after(r.data); }
    catch (e) { toast.error(errMsg(e, "Mise à jour impossible")); }
  };
  const closingEntry = async () => {
    setBusy(true);
    try { const r = await api.post(`${base}/close/year/${end}/closing-entry`); toast.success(`Écriture de fermeture n° ${r.data.entry?.no} — résultat ${money(r.data.net_income)} viré aux bénéfices non répartis`); load(); refresh?.(); }
    catch (e) { toast.error(errMsg(e, "Écriture impossible")); } finally { setBusy(false); }
  };
  const doClose = async () => {
    if (!window.confirm(`Clôturer ${fmtPeriod(kind, end)} ? La période sera verrouillée (plus aucune écriture jusqu'au ${end}).`)) return;
    setBusy(true);
    try { const r = await api.post(`${base}/close/${kind}/${end}`, { force }); toast.success("Période clôturée et verrouillée"); after(r.data); setForce(false); }
    catch (e) { toast.error(errMsg(e, "Clôture impossible")); } finally { setBusy(false); }
  };
  const doReopen = async () => {
    setBusy(true);
    try { const r = await api.post(`${base}/close/${kind}/${end}/reopen`, { reason }); toast.success("Période rouverte"); setReopen(false); setReason(""); after(r.data); }
    catch (e) { toast.error(errMsg(e, "Réouverture impossible")); } finally { setBusy(false); }
  };
  const drive = async () => {
    setBusy(true);
    try { const r = await api.post(`${base}/close/year/${end}/dossier/drive`); toast.success(`Dossier classé dans Drive (${r.data.folder}) — ${r.data.filename}`); load(); }
    catch (e) { toast.error(errMsg(e, "Classement Drive impossible")); } finally { setBusy(false); }
  };
  const action = (id) => {
    if (id === "depreciation") setDepr(true);
    else if (id === "closing_entry") closingEntry();
    else if (id === "dossier") downloadPdf(`${base}/close/year/${end}/dossier.pdf`, `Dossier_fin_exercice_${end}.pdf`, setBusy).then(load);
  };

  const list = kind === "month" ? periods?.months : periods?.years;
  return (
    <div className="space-y-4" data-testid="close-tab">
      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => pickKind("month")} className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${kind === "month" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="close-kind-month"><CalendarDays className="w-3.5 h-3.5" /> Fin de mois</button>
          <button onClick={() => pickKind("year")} className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${kind === "year" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="close-kind-year"><CalendarCheck className="w-3.5 h-3.5" /> Fin d'exercice</button>
        </div>
        <select className={`${inputCls} w-auto`} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="close-period-select">
          {(list || []).map((p) => <option key={p.period_end} value={p.period_end}>{`${fmtPeriod(kind, p.period_end)}${p.closed_at ? " — clôturé" : ""}`}</option>)}
        </select>
        {periods?.locked_until && <Pill cls="bg-amber-100 text-amber-800 border-amber-200" testId="close-locked-until">Verrouillé jusqu'au {periods.locked_until}</Pill>}
        {cl && <span className="text-xs text-slate-500 ml-auto" data-testid="close-progress">{cl.progress.done} / {cl.progress.total} étapes</span>}
      </div>

      {!cl && <p className="text-sm text-slate-400 py-8 text-center">Vérification des données…</p>}
      {cl && (
        <>
          {cl.closed_at && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="close-closed-banner">
              <div className="text-sm text-emerald-900 inline-flex items-center gap-2"><Lock className="w-4 h-4" /> <strong>Clôturé</strong> le {cl.closed_at.slice(0, 10)} par {cl.closed_by}{cl.closing_summary?.forced && <Pill cls="bg-amber-100 text-amber-800 border-amber-200">forcé</Pill>}</div>
              <Btn tone="slate" icon={Unlock} onClick={() => setReopen(true)} testId="close-reopen-btn">Rouvrir…</Btn>
            </div>
          )}
          <div className="space-y-2">
            {cl.steps.map((s, i) => <CloseStep key={s.id} step={s} index={i + 1} locked={!!cl.closed_at} onToggle={setStep} onAction={action} goTab={goTab} scope={scope} />)}
          </div>
          {!cl.closed_at && (
            <div className="bg-white border rounded-xl p-4 flex flex-wrap items-center gap-3 justify-between" data-testid="close-actions">
              <div className="text-xs text-slate-600">
                {cl.ready ? <span className="text-emerald-700 font-semibold">Toutes les étapes sont complètes — vous pouvez clôturer.</span>
                  : <span>Étapes restantes : {cl.blocking.length}. <label className="inline-flex items-center gap-1 ml-2 cursor-pointer"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="close-force" /> clôturer quand même</label></span>}
                {kind === "year" && <div className="text-[11px] text-slate-500 mt-1">La clôture d'exercice passe l'écriture de fermeture (si absente), verrouille la période et prépare le dossier PDF.</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                {kind === "year" && <Btn tone="slate" icon={FileDown} busy={busy} onClick={() => action("dossier")} testId="close-dossier-pdf">Dossier PDF</Btn>}
                {kind === "year" && scope === "books" && <Btn tone="slate" icon={UploadCloud} busy={busy} onClick={drive} testId="close-dossier-drive">Classer dans Drive</Btn>}
                <Btn tone="rose" icon={Lock} busy={busy} disabled={!cl.ready && !force} onClick={doClose} testId="close-do-btn">Clôturer {kind === "month" ? "le mois" : "l'exercice"}</Btn>
              </div>
            </div>
          )}
          {cl.closed_at && kind === "year" && (
            <div className="flex gap-2 flex-wrap">
              <Btn tone="slate" icon={FileDown} busy={busy} onClick={() => action("dossier")} testId="close-dossier-pdf">Dossier PDF</Btn>
              {scope === "books" && <Btn tone="slate" icon={UploadCloud} busy={busy} onClick={drive} testId="close-dossier-drive">Classer dans Drive</Btn>}
            </div>
          )}
        </>
      )}

      {depr && <DepreciationDialog fyEnd={end} onClose={() => setDepr(false)} onPosted={() => { setDepr(false); load(); refresh?.(); }} goTab={goTab} />}
      {reopen && (
        <Modal title="Rouvrir la période" sub="Le verrou est ramené à la clôture précédente ; pour un exercice, l'écriture de fermeture est annulée. Le motif est conservé." onClose={() => setReopen(false)} testId="close-reopen-dialog"
          footer={<><button onClick={() => setReopen(false)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-700" data-testid="close-reopen-cancel">Annuler</button>
            <Btn tone="rose" busy={busy} disabled={reason.trim().length < 3} onClick={doReopen} testId="close-reopen-confirm">Rouvrir</Btn></>}>
          <textarea className={`${inputCls} min-h-[80px]`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (ex. : facture fournisseur reçue en retard)" data-testid="close-reopen-reason" />
        </Modal>
      )}
    </div>
  );
}
